import { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';

// ============================================================================
// CHA-272 — shared agent-tickets view model (Today + sidebar badge + cockpit)
// ============================================================================
// ONE source of truth for every count/exposure figure shown in the shell so
// the KPI strip, the Agents nav badge, and the cockpit can never disagree
// (CHA-239 hard requirement). Read-only: two subscriptions on the existing
// `tenants/inmocean/tickets` collection — open tickets (state new|snoozed,
// both rules) and tickets closed in the last 7 days (single-field range on
// `closed_at`, so no composite index is needed; state filtered client-side).
// InMocean-only (Path 1); tenant path hardcoded like the detection runtime,
// generalization deferred to CHA-25.
const TENANT_ID = 'inmocean';

export const AGENTS = {
  past_cancel: {
    key: 'yulie',
    name: 'Yulie',
    sub: 'past-cancel',
    color: '#6929C4', // carbon-purple-70
    tint: 'rgba(138,63,252,0.08)',
  },
  cost_sheet_gp_drift: {
    key: 'ella',
    name: 'Ella',
    sub: 'GP drift',
    color: '#007D79', // carbon-teal-60
    tint: 'rgba(0,125,121,0.08)',
  },
};

const UNKNOWN_AGENT = { key: 'agent', name: 'Agent', sub: '', color: '#8D8D8D', tint: 'rgba(141,141,141,0.08)' };

export const URGENT = '#DA1E28'; // carbon-red-60
export const WARN = '#FF832B'; // carbon-orange-40

export const agentFor = (rule) => AGENTS[rule] || UNKNOWN_AGENT;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const exposureOf = (t) => num(t?.evidence?.gross_ext_price);

// Severity + row copy are static rule-derived v1 strings (epic constraint:
// no LLM copy). Thresholds mirror the existing lists: Yulie urgent at
// >=14 days past cancel, Ella urgent below 10 GP points.
export const rowViewOf = (t) => {
  const agent = agentFor(t.rule);
  if (t.rule === 'past_cancel') {
    const days = num(t?.evidence?.days_past_cancel);
    const urgent = days >= 14;
    return {
      agent,
      metric: `${days}d`,
      caption: 'past cxl',
      urgent,
      sev: urgent ? URGENT : WARN,
      suggested: urgent
        ? 'ship-or-cancel decision overdue — confirm extension with the buyer'
        : 'past cancel — verify pick / routing before it ages',
    };
  }
  if (t.rule === 'cost_sheet_gp_drift') {
    const gp = num(t?.evidence?.gp_pct);
    const urgent = gp < 10;
    return {
      agent,
      metric: `${gp.toFixed(1)}%`,
      caption: 'GP',
      urgent,
      sev: urgent ? URGENT : WARN,
      suggested: urgent
        ? 'GP collapsed vs quote — check cost-sheet actuals for the worst line'
        : 'GP drifting below plan — compare quoted vs actual uploads',
    };
  }
  return { agent, metric: '—', caption: t.rule || 'ticket', urgent: false, sev: WARN, suggested: 'review in the agents cockpit' };
};

export const styleColorOf = (t) => {
  const e = t?.evidence || {};
  const style = e.style || e.worst_line_style;
  const color = e.color || e.worst_line_color;
  if (!style) return '';
  return color ? `Style ${style} / ${color}` : `Style ${style}`;
};

export const fmtMoney = (n) => `$${Math.round(num(n)).toLocaleString('en-US')}`;

export const fmtMoneyCompact = (n) => {
  const v = num(n);
  if (v >= 999500) return `$${(v / 1e6).toFixed(2)}M`; // 999.5K+ rounds up to M, never "$1000K"
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
};

const ET = 'America/New_York';

export const timeLabel = (d, now = new Date()) => {
  const date = toDate(d);
  if (!date) return '—';
  const fmtDay = (x) => x.toLocaleDateString('en-US', { timeZone: ET });
  if (fmtDay(date) === fmtDay(now)) {
    return date.toLocaleTimeString('en-US', { timeZone: ET, hour: '2-digit', minute: '2-digit', hour12: false });
  }
  const yd = new Date(now.getTime() - 864e5);
  if (fmtDay(date) === fmtDay(yd)) return 'yd';
  return date.toLocaleDateString('en-US', { timeZone: ET, month: 'numeric', day: 'numeric' });
};

// Worklist ranking: new before snoozed, urgent before warning, then biggest
// exposure first — "what needs a call" order, deterministic and explainable.
export const rankOpen = (tickets) =>
  [...tickets].sort((a, b) => {
    const stateRank = (t) => (t.state === 'new' ? 0 : 1);
    if (stateRank(a) !== stateRank(b)) return stateRank(a) - stateRank(b);
    const ua = rowViewOf(a).urgent ? 0 : 1;
    const ub = rowViewOf(b).urgent ? 0 : 1;
    if (ua !== ub) return ua - ub;
    return exposureOf(b) - exposureOf(a);
  });

const reasonOf = (t) => {
  const r = String(t.closed_reason || '');
  return r.startsWith('manual: ') ? r.slice(8) : r.replace(/^manual:\s*/, '') || r;
};

export const buildFeed = (open, cleared, now = new Date()) => {
  const events = [];
  const recent = (d, hours) => {
    const date = toDate(d);
    if (!date) return false;
    const diff = now.getTime() - date.getTime();
    return diff >= 0 && diff <= hours * 3600e3; // future-dated writes are not "recent"
  };
  for (const t of cleared) {
    const at = toDate(t.closed_at);
    if (!at) continue;
    const manual = String(t.closed_reason || '').startsWith('manual:');
    const agent = agentFor(t.rule);
    events.push({
      at,
      who: manual ? 'You' : agent.name,
      whoColor: manual ? '#161616' : agent.color,
      text: manual
        ? `marked PO ${t.po_no || t.id} OK — "${reasonOf(t)}"`
        : `auto-closed PO ${t.po_no || t.id} — resolved`,
    });
  }
  for (const t of open) {
    const agent = agentFor(t.rule);
    if (t.state === 'new' && recent(t.created_at, 48)) {
      events.push({
        at: toDate(t.created_at),
        who: agent.name,
        whoColor: agent.color,
        text: `flagged PO ${t.po_no || t.id} — ${rowViewOf(t).metric} ${rowViewOf(t).caption}`,
      });
    }
    if (t.state === 'snoozed' && recent(t.snoozed_at, 48)) {
      events.push({ at: toDate(t.snoozed_at), who: 'You', whoColor: '#161616', text: `snoozed PO ${t.po_no || t.id}` });
    }
  }
  return events
    .sort((a, b) => b.at - a.at)
    .slice(0, 6)
    .map((e) => ({ ...e, time: timeLabel(e.at, now) }));
};

// The full derived view model. Pure — unit-testable without Firestore.
export const buildViewModel = (open, cleared, now = new Date()) => {
  // Clamp to a true rolling 7 days at DERIVE time: the Firestore range is
  // fixed at subscribe time, so a long-lived tab would otherwise inflate the
  // "this week" window past 7 days.
  const weekAgo = now.getTime() - 7 * 864e5;
  const closedThisWeek = cleared.filter((t) => {
    if (t.state !== 'closed') return false;
    const at = toDate(t.closed_at);
    return at && at.getTime() >= weekAgo;
  });
  const openExposure = open.reduce((s, t) => s + exposureOf(t), 0);
  const clearedExposure = closedThisWeek.reduce((s, t) => s + exposureOf(t), 0);
  const urgentCount = open.filter((t) => rowViewOf(t).urgent).length;

  const clearDurations = closedThisWeek
    .map((t) => {
      const a = toDate(t.created_at);
      const b = toDate(t.closed_at);
      return a && b ? (b - a) / 864e5 : null;
    })
    .filter((d) => d != null && d >= 0);
  const avgClearDays = clearDurations.length
    ? clearDurations.reduce((s, d) => s + d, 0) / clearDurations.length
    : null;

  const agents = Object.entries(AGENTS).map(([rule, meta]) => {
    const mine = open.filter((t) => t.rule === rule);
    const lastChecked = mine
      .map((t) => toDate(t.last_checked))
      .filter(Boolean)
      .sort((a, b) => b - a)[0] || null;
    const newToday = mine.filter((t) => {
      const c = toDate(t.created_at);
      return c && now.getTime() - c.getTime() <= 24 * 3600e3;
    }).length;
    return { ...meta, rule, openCount: mine.length, newToday, lastRun: lastChecked };
  });
  // Note: `newToday` is a rolling 24h window (the card renders it as "new").
  // `lastRun` is null in practice today: the detectors write last_checked to
  // po_records, NOT to tickets (verified in pastCancelDetect.js), so the card
  // omits the "ran HH:MM" segment rather than claiming 'idle'. Real per-run
  // stats need a run doc the runtime doesn't write (flagged on CHA-239).

  return {
    open,
    // CHA-588 — the Work list (#/work) shows a Closed group and reopen targets closed findings.
    // These are ALREADY fetched by the existing `cleared` subscription (no new subscription is
    // added); `closedThisWeek` is the same rolling-7d closed set the Today tiles use.
    closed: closedThisWeek,
    ranked: rankOpen(open),
    openCount: open.length,
    urgentCount,
    openExposure,
    clearedCount: closedThisWeek.length,
    clearedExposure,
    avgClearDays,
    agents,
    feed: buildFeed(open, closedThisWeek, now),
  };
};

export default function useAgentTickets({ enabled = true } = {}) {
  const [open, setOpen] = useState([]);
  const [cleared, setCleared] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setOpen([]);
      setCleared([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const db = getFirestore();
    let qOpen;
    let qCleared;
    try {
      const col = collection(db, `tenants/${TENANT_ID}/tickets`);
      qOpen = query(col, where('state', 'in', ['new', 'snoozed']));
      qCleared = query(col, where('closed_at', '>=', new Date(Date.now() - 7 * 864e5)));
    } catch (e) {
      console.error('shell: failed to build tickets queries', e);
      setError('Could not load the agent queue.');
      setLoading(false);
      return;
    }

    // Same safety net as YuliePage: never hang the shell on a silent Firestore
    // stall — surface a soft notice, let a late snapshot clear it.
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError((prev) => prev || 'Still loading — Firestore is slow to respond.');
    }, 8000);

    const unsubOpen = onSnapshot(
      qOpen,
      (snap) => {
        clearTimeout(timeoutId);
        setOpen(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setError(null);
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('shell: open-tickets subscription failed', err);
        setError(
          err?.code === 'failed-precondition'
            ? 'The agent queue needs a Firestore index — see console.'
            : 'Live agent queue unavailable.'
        );
        setLoading(false);
      }
    );

    const unsubCleared = onSnapshot(
      qCleared,
      (snap) => setCleared(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        // Non-fatal: Today still works without the cleared-this-week tile data.
        console.error('shell: cleared-tickets subscription failed', err);
        setCleared([]);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubOpen();
      unsubCleared();
    };
  }, [enabled]);

  // Hourly tick so time-window derivations (rolling 7d / 24h / 'yd' labels)
  // can't go stale in a long-lived tab between Firestore snapshots.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 3600e3);
    return () => clearInterval(id);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const view = useMemo(() => buildViewModel(open, cleared), [open, cleared, tick]);
  return { ...view, loading, error };
}
